/**
 * Shared Markdown renderer used by both Rules and Guide pages.
 *
 * What's supported (via react-markdown + remark-gfm + rehype-raw):
 *   - Bold          **like this**
 *   - Italic        *like this*
 *   - Underline     <u>like this</u>   (raw HTML tag; rehype-raw lets it through)
 *   - Headers       # H1 through ###### H6
 *   - Lists         - bullet   /   1. numbered
 *   - Links         [text](url)
 *   - Inline code   `code`
 *   - Code fences   ```lang
 *   - Strikethrough ~~strike~~
 *   - Tables        via GFM pipe syntax
 *
 * `inline` mode strips block-level wrappers so it can be dropped inside a
 * flexbox row / list item without extra vertical margins.
 */
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

const BASE = {
  a: (props) => (
    <a
      {...props}
      target="_blank"
      rel="noopener noreferrer"
      className="text-[#C0A875] underline decoration-[#C0A875]/40 hover:decoration-[#C0A875]"
    />
  ),
  strong: (props) => <strong {...props} className="text-[#E2E4DF] font-semibold" />,
  em: (props) => <em {...props} className="italic" />,
  code: ({ inline, className, children, ...rest }) =>
    inline ? (
      <code
        {...rest}
        className="bg-[#0D110C] border border-[#283524] text-[#C0A875] px-1.5 py-0.5 font-mono-ui text-[0.9em]"
      >
        {children}
      </code>
    ) : (
      <code {...rest} className={`${className || ""} font-mono-ui text-xs`}>
        {children}
      </code>
    ),
  del: (props) => <del {...props} className="text-[#5C6B51]" />,
};

const BLOCK = {
  ...BASE,
  h1: (props) => <h1 {...props} className="font-display uppercase text-[#E2E4DF] text-2xl tracking-tight mt-4 first:mt-0" />,
  h2: (props) => <h2 {...props} className="font-display uppercase text-[#E2E4DF] text-xl tracking-tight mt-4 first:mt-0" />,
  h3: (props) => <h3 {...props} className="font-display uppercase text-[#E2E4DF] text-lg tracking-tight mt-3 first:mt-0" />,
  h4: (props) => <h4 {...props} className="font-display uppercase text-[#C0A875] text-base tracking-tight mt-3 first:mt-0" />,
  h5: (props) => <h5 {...props} className="font-mono-ui uppercase tracking-[0.25em] text-[#C0A875] text-[11px] mt-3 first:mt-0" />,
  h6: (props) => <h6 {...props} className="font-mono-ui uppercase tracking-[0.25em] text-[#8D9A87] text-[10px] mt-2 first:mt-0" />,
  p: (props) => <p {...props} className="leading-relaxed" />,
  ul: (props) => <ul {...props} className="list-disc pl-5 space-y-1 marker:text-[#5C6B51]" />,
  ol: (props) => <ol {...props} className="list-decimal pl-5 space-y-1 marker:text-[#5C6B51]" />,
  li: (props) => <li {...props} className="leading-relaxed" />,
  blockquote: (props) => (
    <blockquote {...props} className="border-l-2 border-[#C0A875] pl-3 text-[#A8B5A0] italic" />
  ),
  pre: (props) => (
    <pre {...props} className="bg-[#0D110C] border border-[#283524] p-3 overflow-x-auto font-mono-ui text-xs" />
  ),
};

// `inline` variant: no p/h/ul wrappers rendered as block. Paragraphs collapse
// to inline spans so a single markdown line can appear inside a flex row.
const INLINE = {
  ...BASE,
  p: (props) => <span {...props} />,
};

export default function Markdown({ children, inline = false, className = "" }) {
  if (!children) return null;
  const components = inline ? INLINE : BLOCK;
  const content = (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
      components={components}
    >
      {String(children)}
    </ReactMarkdown>
  );
  if (inline) {
    return <span className={className}>{content}</span>;
  }
  return <div className={`space-y-2 ${className}`}>{content}</div>;
}
